import { apiService } from "../api.service";

// Domain Models
import {
  Worker,
  WorkerSchedule,
  WorkDay,
  Service,
  Pagination
} from "../../models/app/domain";

// Requests
import { ServiceRequest } from "../../models/api/requests";

// Constants
import * as filtersTypes from "../../constants/services.constants";

// Utils
import { isEmpty } from "../../utils";

/**
 * Fetch services Request
 * @return - ServicesResponse || ErrorResponse
 */
export const fetchServicesRequest = async (
  dateFilter,
  groupQuery,
  searchQuery,
  limit,
  lastService,
  sortBy
) => {
  const params = new URLSearchParams();
  params.set("pagination[per_page]", limit);

  const statuses = Array.isArray(groupQuery)
    ? groupQuery
    : groupQuery
    ? [groupQuery]
    : [];

  // Send each selected chip as `statuses[i]=...`. BE interprets the array as
  // an OR-of-chips filter (status match, unassigned, notifications). When the
  // array is empty, no `statuses` params are sent and the BE falls back to the
  // legacy single-group default (`group=all`).
  if (statuses.length > 0) {
    statuses.forEach((s, i) => params.set(`statuses[${i}]`, s));
  }

  if (!isEmpty(searchQuery)) {
    params.set("q", searchQuery);
  }

  if (
    dateFilter &&
    dateFilter.enabled &&
    dateFilter.from &&
    dateFilter.to
  ) {
    params.set("date", dateFilter.from);
    params.set("date_to", dateFilter.to);
    if (dateFilter.mode) {
      params.set("date_mode", dateFilter.mode);
    }
  }

  if (!isEmpty(sortBy)) {
    params.set("sort", sortBy);
  }

  if (!isEmpty(lastService)) {
    params.set("pagination[from]", lastService);
  }

  const query = `?${params.toString()}`;
  const url = `/services${query}`;

  const response = await apiService.get(url);
  if (response.hasError) {
    return response;
  }
  return {
    services: response.data.items.map(service => new Service(service)),
    pagination: new Pagination(response.data.pagination)
  };
};

/**
 * Fetch service  Request
 * @return - ServiceResponse || ErrorResponse
 */
export const fetchServiceDetailsRequest = async (serviceId, branchId) => {
  const response = await apiService.get(`/services/${serviceId}`, branchId);
  if (response.hasError) {
    return response;
  }
  return { service: new Service(response.data.service) };
};

/**
 * Cancel service  Request
 * @return - MessageResponse || ErrorResponse
 */
export const cancelServiceRequest = async serviceId => {
  const response = await apiService.post(`/services/${serviceId}/cancel`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Start service  Request
 * @return - MessageResponse || ErrorResponse
 */
export const startServiceRequest = async serviceId => {
  const response = await apiService.post(`/services/${serviceId}/start`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Pause service  Request
 * @return - MessageResponse || ErrorResponse
 */
export const pauseServiceRequest = async serviceId => {
  const response = await apiService.post(`/services/${serviceId}/pause`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Resume service  Request
 * @return - MessageResponse || ErrorResponse
 */
export const resumeServiceRequest = async serviceId => {
  const response = await apiService.post(`/services/${serviceId}/resume`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Finish service  Request
 * @return - MessageResponse || ErrorResponse
 */
export const finishServiceRequest = async serviceId => {
  const response = await apiService.post(`/services/${serviceId}/finish`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Update service Request
 * @return - WorkersResponse || ErrorResponse
 */
export const updateServiceRequest = async (formValues, serviceId) => {
  const payload = new ServiceRequest(formValues);
  payload.removeUnusedValues();
  const response = await apiService.patch(`/services/${serviceId}`, payload);
  if (response.hasError) {
    return response;
  }
  return { service: new Service(response.data.service) };
};

// SCHEDULE requests
/**
 * Fetch workers Request
 * @return - WorkersResponse || ErrorResponse
 */
export const fetchWorkersRequest = async queryParams => {
  let query = "";
  if (queryParams) {
    query = `?query=${queryParams}`;
  }
  const response = await apiService.get(`/branches/staff/workers${query}`);
  if (response.hasError) {
    return response;
  }
  return { workers: response.data.workers.map(worker => new Worker(worker)) };
};

/**
 * Fetch workers schedule Request
 * @param date - YYYY-MM-DD string
 * @param statuses - array of ERP status strings (accepted/in-progress/paused/finished/canceled).
 *                   Empty array → defaults to the 3 active statuses for back-compat.
 * @param chosenEmployees - optional list of selected workers; empty = all.
 * @return - WorkersScheduleResponse || ErrorResponse
 */
export const fetchWorkersScheduleRequest = async (
  date,
  statuses,
  chosenEmployees
) => {
  const params = new URLSearchParams();
  params.set("with_working_hours", "true");
  params.set("date", date);

  const list =
    Array.isArray(statuses) && statuses.length > 0
      ? statuses
      : [
          filtersTypes.FILTERS_ACCEPTED,
          filtersTypes.FILTERS_IN_PROGRESS,
          filtersTypes.FILTERS_PAUSED
        ];
  list.forEach((s, i) => params.set(`statuses[${i}]`, s));

  if (Array.isArray(chosenEmployees)) {
    chosenEmployees.forEach((employee, i) =>
      params.set(`workers[${i}]`, employee.id)
    );
  }

  const response = await apiService.get(`/services/schedule?${params.toString()}`);
  if (response.hasError) {
    return response;
  }
  return {
    employees: response.data.workers.map(
      workerSchedule => new WorkerSchedule(workerSchedule)
    ),
    workDay: response.data.work_day
      ? new WorkDay(response.data.work_day)
      : null
  };
};
